/**
 * Resistor networks: series and parallel combinations, nested to any depth, and the
 * voltage and current each resistor sees when a supply is connected.
 *
 * A network is a tree. The expression syntax is `+` for series and `|` for parallel,
 * with `|` binding tighter, brackets to group, and values in ohms with an optional SI
 * suffix or the RKM code printed on parts: `4.7k`, `2M2`, `4R7`, `100 + (220 | 330)`.
 */

export type Network =
  | { kind: "leaf"; ohms: number }
  | { kind: "series" | "parallel"; children: Network[] };

export type ParseErrorCode =
  | "empty"
  | "bad-character"
  | "bad-number"
  | "not-positive"
  | "missing-paren"
  | "unexpected-end"
  | "unexpected-token";

export type ParseResult =
  | { ok: true; network: Network }
  | { ok: false; error: ParseErrorCode; token: string };

const MAX_LEAVES = 40;
const MAX_DEPTH = 12;

/** `4.7k`, `2k2`, `4R7`, `220`, `0.47`, optionally followed by Ω. */
const NUMBER_TOKEN = /^(\d+(?:[.,]\d+)?)([pnuµmkKMGR]?)(\d*)\s*Ω?/;
const SPACE = /\s/;
const MULTIPLIERS: Record<string, number> = {
  p: -12,
  n: -9,
  u: -6,
  µ: -6,
  m: -3,
  R: 0,
  k: 3,
  K: 3,
  M: 6,
  G: 9,
};

/** The ohms an `NUMBER_TOKEN` match stands for, or null when it is not a number. */
function tokenValue(match: RegExpExecArray): number | null {
  const [, whole, suffix, fraction] = match;
  if (fraction !== "" && suffix === "") {
    return null;
  }
  const mantissa = `${whole.replace(",", ".")}${fraction === "" ? "" : `.${fraction}`}`;
  if (fraction !== "" && whole.includes(".")) {
    return null;
  }
  const exp = suffix === "" ? 0 : MULTIPLIERS[suffix];
  const value = Number(`${mantissa}e${exp}`);
  return Number.isFinite(value) ? value : null;
}

class Parser {
  private pos = 0;
  private leaves = 0;
  private readonly text: string;

  constructor(text: string) {
    this.text = text;
  }

  private skip(): void {
    while (this.pos < this.text.length && SPACE.test(this.text[this.pos])) {
      this.pos += 1;
    }
  }

  private peek(): string {
    this.skip();
    return this.text[this.pos] ?? "";
  }

  parse(): ParseResult {
    if (this.text.trim() === "") {
      return { ok: false, error: "empty", token: "" };
    }
    const result = this.series(0);
    if (!result.ok) {
      return result;
    }
    const rest = this.peek();
    if (rest !== "") {
      return {
        ok: false,
        error: rest === ")" ? "missing-paren" : "unexpected-token",
        token: rest,
      };
    }
    return result;
  }

  private series(depth: number): ParseResult {
    const first = this.parallel(depth);
    if (!first.ok) {
      return first;
    }
    const children: Network[] = [first.network];
    while (this.peek() === "+") {
      this.pos += 1;
      const next = this.parallel(depth);
      if (!next.ok) {
        return next;
      }
      children.push(next.network);
    }
    return { ok: true, network: group("series", children) };
  }

  private parallel(depth: number): ParseResult {
    const first = this.atom(depth);
    if (!first.ok) {
      return first;
    }
    const children: Network[] = [first.network];
    while (this.peek() === "|") {
      this.pos += 1;
      if (this.peek() === "|") {
        this.pos += 1;
      }
      const next = this.atom(depth);
      if (!next.ok) {
        return next;
      }
      children.push(next.network);
    }
    return { ok: true, network: group("parallel", children) };
  }

  private atom(depth: number): ParseResult {
    const ch = this.peek();
    if (ch === "") {
      return { ok: false, error: "unexpected-end", token: "" };
    }
    if (ch === "(") {
      if (depth >= MAX_DEPTH) {
        return { ok: false, error: "unexpected-token", token: ch };
      }
      this.pos += 1;
      const inner = this.series(depth + 1);
      if (!inner.ok) {
        return inner;
      }
      if (this.peek() !== ")") {
        return { ok: false, error: "missing-paren", token: "(" };
      }
      this.pos += 1;
      return inner;
    }
    return this.number();
  }

  private number(): ParseResult {
    const rest = this.text.slice(this.pos);
    const match = NUMBER_TOKEN.exec(rest);
    if (match === null) {
      const bad = rest[0] ?? "";
      const isOperator = "+|)".includes(bad);
      return {
        ok: false,
        error: isOperator ? "unexpected-token" : "bad-character",
        token: bad,
      };
    }
    const value = tokenValue(match);
    if (value === null) {
      return { ok: false, error: "bad-number", token: match[0].trim() };
    }
    if (value <= 0) {
      return { ok: false, error: "not-positive", token: match[0].trim() };
    }
    this.leaves += 1;
    if (this.leaves > MAX_LEAVES) {
      return { ok: false, error: "unexpected-token", token: match[0].trim() };
    }
    this.pos += match[0].length;
    return { ok: true, network: { kind: "leaf", ohms: value } };
  }
}

/** One child stays itself; same-kind children of a same-kind parent merge into it. */
function group(kind: "series" | "parallel", children: Network[]): Network {
  if (children.length === 1) {
    return children[0];
  }
  const flat = children.flatMap((child) =>
    child.kind === kind ? child.children : [child]
  );
  return { kind, children: flat };
}

export function parseNetwork(text: string): ParseResult {
  return new Parser(text).parse();
}

/** A plain list of resistors joined one way: the Series and Parallel tabs. */
export function listNetwork(
  kind: "series" | "parallel",
  values: readonly number[]
): Network | null {
  if (values.length === 0) {
    return null;
  }
  return values.length === 1
    ? { kind: "leaf", ohms: values[0] }
    : { kind, children: values.map((ohms) => ({ kind: "leaf", ohms })) };
}

export function equivalent(network: Network): number {
  if (network.kind === "leaf") {
    return network.ohms;
  }
  const values = network.children.map(equivalent);
  if (network.kind === "series") {
    return values.reduce((sum, value) => sum + value, 0);
  }
  return 1 / values.reduce((sum, value) => sum + 1 / value, 0);
}

/** The leaves left to right: the numbering the results table uses (R1, R2, …). */
export function leavesOf(network: Network): number[] {
  return network.kind === "leaf"
    ? [network.ohms]
    : network.children.flatMap(leavesOf);
}

export interface LeafLoad {
  ohms: number;
  volts: number;
  amps: number;
  watts: number;
}

/** Voltage, current and power in each resistor for a supply across the whole network. */
export function analyse(network: Network, supply: number): LeafLoad[] {
  if (network.kind === "leaf") {
    const amps = supply / network.ohms;
    return [{ ohms: network.ohms, volts: supply, amps, watts: supply * amps }];
  }
  if (network.kind === "parallel") {
    return network.children.flatMap((child) => analyse(child, supply));
  }
  const total = equivalent(network);
  return network.children.flatMap((child) =>
    analyse(child, (supply * equivalent(child)) / total)
  );
}

/** A sub-network that combines two or more parts, in the order it is worked out. */
export interface Combination {
  kind: "series" | "parallel";
  parts: number[];
  result: number;
}

/** Every combination, innermost first, so the working reads from the inside out. */
export function combinations(network: Network): Combination[] {
  if (network.kind === "leaf") {
    return [];
  }
  const inner = network.children.flatMap(combinations);
  return [
    ...inner,
    {
      kind: network.kind,
      parts: network.children.map(equivalent),
      result: equivalent(network),
    },
  ];
}

export interface VoltageDivider {
  vout: number;
  current: number;
  /** R2 ÷ (R1 + R2). */
  ratio: number;
}

export function voltageDivider(
  vin: number,
  r1: number,
  r2: number
): VoltageDivider {
  const ratio = r2 / (r1 + r2);
  return { vout: vin * ratio, current: vin / (r1 + r2), ratio };
}

/** The R2 that makes the divider give `vout` from `vin`, or null unless 0 < vout < vin. */
export function dividerPartner(
  vin: number,
  vout: number,
  r1: number
): number | null {
  if (!(vout > 0 && vout < vin && r1 > 0)) {
    return null;
  }
  return (r1 * vout) / (vin - vout);
}

export interface CurrentDivider {
  i1: number;
  i2: number;
  volts: number;
}

/** Total current `total` splits over R1 and R2 in parallel: each gets the other's share. */
export function currentDivider(
  total: number,
  r1: number,
  r2: number
): CurrentDivider {
  const i1 = (total * r2) / (r1 + r2);
  return { i1, i2: total - i1, volts: i1 * r1 };
}
